'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
	advanceCandidateAction,
	autoAdvanceSeconds,
	type CandidateActionResult,
	candidateTotalTime,
	canWriteCandidate,
	createViewTracker,
	finishCandidateAction,
	formatRemaining,
	getCandidateAttemptAction,
	getCandidateMessagesAction,
	hasPromoCreditsOutcome,
	isOlderAttemptSnapshot,
	remainingSeconds,
	resultHref,
	retainDialogSnapshot,
	synchronizeClockOffset,
	unwrapCandidateResult,
	useCandidateAnalytics,
} from '@/features/candidate-interview';
import { useConfirm } from '@/shared';
import type {
	AIVisibleMessageV1,
	CandidateInterviewAttemptV1,
	CandidateInterviewV1,
} from '@/shared/api';

import { CandidatePromoOutcome } from './candidate-promo-outcome';
import { InterviewSession } from './interview-session';

type Props = { interview: CandidateInterviewV1; completionHref?: string };
export function CandidateInterviewSession({
	interview,
	completionHref,
}: Props) {
	const router = useRouter();
	const track = useCandidateAnalytics(interview.id, 'session');
	const seen = useRef(createViewTracker());
	const confirm = useConfirm();
	const [attempt, setAttempt] = useState(interview.attempt);
	const [dialogSnapshot, setDialogSnapshot] = useState(
		interview.attempt?.dialog,
	);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const [now, setNow] = useState(Date.now);
	const [offset, setOffset] = useState(() =>
		interview.attempt
			? Date.parse(interview.attempt.serverNow) - Date.now()
			: 0,
	);
	const [history, setHistory] = useState<{
		id: string;
		messages: AIVisibleMessageV1[];
	} | null>(null);
	const inFlight = useRef(false);
	const mutating = useRef(false);
	const [historyRetry, setHistoryRetry] = useState(0);
	const mounted = useRef(true);
	const attemptRef = useRef(attempt);
	const accept = useCallback((next: CandidateInterviewAttemptV1) => {
		if (!mounted.current || isOlderAttemptSnapshot(attemptRef.current, next))
			return;
		const sameAttempt =
			attemptRef.current?.id.toLowerCase() === next.id.toLowerCase();
		const receivedAt = Date.now();
		attemptRef.current = next;
		setAttempt(next);
		setDialogSnapshot((previous) =>
			retainDialogSnapshot(previous, next.dialog),
		);
		setOffset((previous) =>
			synchronizeClockOffset(
				sameAttempt ? previous : undefined,
				next.serverNow,
				receivedAt,
			),
		);
		setNow(receivedAt);
		setError('');
	}, []);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 250);
		return () => clearInterval(timer);
	}, []);
	const refresh = useCallback(async () => {
		const current = attemptRef.current;
		if (!current || inFlight.current || mutating.current) return;
		inFlight.current = true;
		try {
			const next = unwrapCandidateResult(
				await getCandidateAttemptAction(current.id),
			);
			if (attemptRef.current?.id.toLowerCase() === current.id.toLowerCase())
				accept(next);
		} catch (e) {
			if (mounted.current)
				setError(
					e instanceof Error ? e.message : 'Не удалось обновить интервью.',
				);
		} finally {
			inFlight.current = false;
		}
	}, [accept]);
	const attemptId = attempt?.id;
	const attemptStatus = attempt?.status;
	useEffect(() => {
		if (!attemptId || attemptStatus !== 'in_progress') return;
		const timer = setInterval(() => void refresh(), 2000);
		window.addEventListener('online', refresh);
		window.addEventListener('focus', refresh);
		return () => {
			clearInterval(timer);
			window.removeEventListener('online', refresh);
			window.removeEventListener('focus', refresh);
		};
	}, [attemptId, attemptStatus, refresh]);
	const dialogId = dialogSnapshot?.id.toLowerCase();
	useEffect(() => {
		if (!dialogId) return;
		let cancelled = false;
		getCandidateMessagesAction(dialogId)
			.then(unwrapCandidateResult)
			.then((messages) => {
				if (!cancelled) setHistory({ id: dialogId, messages });
			})
			.catch((e) => {
				if (!cancelled)
					setError(
						e instanceof Error
							? e.message
							: 'Не удалось загрузить диалог. Нажмите «Обновить».',
					);
			});
		return () => {
			cancelled = true;
		};
	}, [dialogId, historyRetry]);
	const mutate = useCallback(
		async (
			action: () => Promise<CandidateActionResult<CandidateInterviewAttemptV1>>,
			operation: 'advance' | 'finish',
		) => {
			if (mutating.current) return false;
			mutating.current = true;
			setBusy(true);
			setError('');
			try {
				const next = unwrapCandidateResult(await action());
				if (operation === 'advance')
					track('advance_success', { position: next.currentPosition });
				accept(next);
				return true;
			} catch (e) {
				setError(
					e instanceof Error ? e.message : 'Не удалось выполнить действие.',
				);
				return false;
			} finally {
				mutating.current = false;
				setBusy(false);
			}
		},
		[accept, track],
	);
	const advance = useCallback(
		async (skip: boolean, automatic = false) => {
			if (!attempt) return false;
			track(skip ? 'skip_intent' : 'next_intent', {
				position: attempt.currentPosition,
				action: automatic ? 'timeout' : skip ? 'skip' : 'next',
			});
			return await mutate(
				() =>
					advanceCandidateAction(
						attempt.id,
						attempt.currentPosition,
						attempt.version,
						skip,
					),
				'advance',
			);
		},
		[attempt, mutate, track],
	);
	const autoNextSeconds = attempt
		? autoAdvanceSeconds(attempt, now, offset)
		: undefined;
	useEffect(() => {
		if (autoNextSeconds !== 0 || busy || error) return;
		const timer = setTimeout(() => void advance(false, true), 0);
		return () => clearTimeout(timer);
	}, [autoNextSeconds, busy, error, advance]);
	const finish = async () => {
		if (!attempt) return false;
		track('finish_intent');
		const ok = await confirm('', {
			title: 'Завершить интервью?',
			description: 'Продолжить эту попытку будет нельзя.',
			confirmText: 'Завершить',
		});
		if (ok)
			return await mutate(() => finishCandidateAction(attempt.id), 'finish');
		track('finish_cancel');
		return false;
	};
	const item = attempt?.items[attempt.currentPosition];
	const totalTime = attempt
		? candidateTotalTime(attempt, now, offset)
		: formatRemaining(
				interview.targetDurationSeconds ?? interview.totalSeconds,
			);
	const seconds = item ? remainingSeconds(item.deadlineAt, now, offset) : 0;
	const completedHref = resultHref(interview.id, attempt, completionHref);
	const exhausted = hasPromoCreditsOutcome(attempt);
	useEffect(() => {
		if (seen.current('session')) track('session_view');
		if (!exhausted && completedHref && seen.current('completed')) {
			track('finish_success');
			router.replace(completedHref);
		}
	}, [completedHref, exhausted, router, track]);
	useEffect(() => {
		if (error && seen.current(`error:${historyRetry}`))
			track('request_error', { action: 'session', outcome: 'request_failed' });
	}, [error, historyRetry, track]);
	const writable =
		!!attempt && !busy && !error && canWriteCandidate(attempt, now, offset);
	const errorBanner = error ? (
		<div
			role="alert"
			className="mx-6 my-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800"
		>
			{error}{' '}
			<button
				className="underline"
				onClick={() => {
					track('retry', { action: 'session' });
					setHistoryRetry((value) => value + 1);
					void refresh();
				}}
			>
				Обновить
			</button>
		</div>
	) : null;

	if (exhausted && attempt)
		return (
			<CandidatePromoOutcome
				interviewId={interview.id}
				url={attempt.salesLandingUrl}
				outcome="credits_exhausted"
			/>
		);
	if (completedHref)
		return (
			<div role="status" className="p-8 text-center text-gray-500">
				Открываем результат…
			</div>
		);

	return (
		<div className="flex h-full min-h-0 flex-col">
			{errorBanner}
			<div className="min-h-0 flex-1">
				{attempt?.problem &&
				dialogSnapshot &&
				history &&
				history.id === dialogId ? (
					<InterviewSession
						key={dialogId}
						problemId={attempt.problem.id}
						problem={attempt.problem}
						dialogId={dialogSnapshot.id}
						initialDialog={dialogSnapshot}
						initialMessages={history.messages}
						onRestart={() => {}}
						nextHref={`/interviews/${interview.id}`}
						nextLabel="Следующая задача"
						breadcrumbs={[{ label: interview.title }]}
						candidate={{
							canWrite: writable,
							secondsLeft: seconds,
							autoNextSeconds,
							progress: {
								current: attempt.currentPosition + 1,
								total: attempt.items.length,
							},
							totalTime,
							closed: item?.status !== 'active' || seconds === 0,
							timedOut:
								item?.status === 'timed_out' ||
								(item?.status === 'active' && seconds === 0),
							busy,
							onSkip: () => advance(true),
							onNext: () => advance(false),
							onFinish: finish,
						}}
					/>
				) : (
					<div className="p-8 text-center text-gray-3">Загружаем диалог…</div>
				)}
			</div>
		</div>
	);
}
